import { ref, onBeforeUnmount } from 'vue';
import { useMapGetter } from './store';
import VersionAPI from '../api/version';

/**
 * Composable for checking if a new version has been deployed.
 * Polls the version endpoint and compares with the loaded git SHA.
 * @param {number} interval - Polling interval in milliseconds (default: 60000 = 1 minute)
 * @returns {Object} An object containing version check state and methods.
 */
export const useVersionCheck = (interval = 60000) => {
  const globalConfig = useMapGetter('globalConfig/get');
  const hasNewVersion = ref(false);
  const timeoutId = ref(null);
  const loadedGitSha = ref(globalConfig.value.gitSha);

  const checkVersion = async () => {
    try {
      const response = await VersionAPI.get();
      const currentGitSha = response.data.git_sha;

      // If the git SHA has changed, mark that a new version is available
      if (currentGitSha && currentGitSha !== loadedGitSha.value) {
        hasNewVersion.value = true;
      }
    } catch (error) {
      // Silently fail - don't disrupt user experience if version check fails
      // Version check errors are expected and should not be logged
    }
  };

  const startPolling = () => {
    checkVersion();

    timeoutId.value = setInterval(async () => {
      await checkVersion();
    }, interval);
  };

  const stopPolling = () => {
    if (timeoutId.value) {
      clearInterval(timeoutId.value);
      timeoutId.value = null;
    }
  };

  const dismissNewVersion = () => {
    hasNewVersion.value = false;
  };

  onBeforeUnmount(() => {
    stopPolling();
  });

  return {
    hasNewVersion,
    startPolling,
    stopPolling,
    dismissNewVersion,
  };
};
